import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BudgetPanelContent } from '@/features/canvas/components/BudgetPanel/BudgetPanelContent';
import { ViewToggle } from '@/features/canvas/components/ViewToggle';
import { BudgetViewTab, useEditorContext } from '@/features/canvas/contexts/editor-context';

const BudgetSidebar = () => {
  const { t } = useTranslation();
  const { budgetViewTab, setBudgetViewTab, popupVisible } = useEditorContext();

  // Check field visibility using popupVisible from EditorContext (like LinkedHideFieldIcon does)
  const fieldVisibility = useMemo(() => {
    // Check if capex/opex are visible using popupVisible (standard budget fields)
    const isCapexVisible = popupVisible.some(f => f.title === 'CapEx' && f.type === 'capex');
    const isOpexVisible = popupVisible.some(f => f.title === 'OpEx' && f.type === 'opex');

    // Since CapEx/OpEx are linked, both should have the same visibility state
    // Show the section if either is visible (they should always be synced)
    const isCapexOpexVisible = isCapexVisible || isOpexVisible;

    // Check if spend is visible using popupVisible
    const isSpendVisible = popupVisible.some(f => f.title === 'Spent' && f.type === 'spend');

    return {
      isCapexOpexVisible,
      isSpendVisible,
    };
  }, [popupVisible]);

  // Build tabs array based on visibility
  const tabs = useMemo(() => {
    const allTabs: { label: string; value: BudgetViewTab }[] = [
      { value: 'total', label: t('editor.sidebar.initiative.budget.total') },
    ];

    if (fieldVisibility.isSpendVisible) {
      allTabs.push({ value: 'spend', label: t('editor.sidebar.initiative.budget.expenditure') });
    }

    if (fieldVisibility.isCapexOpexVisible) {
      allTabs.push({
        value: 'capex-opex',
        label: t('editor.sidebar.initiative.budget.capex_opex'),
      });
    }

    return allTabs;
  }, [fieldVisibility, t]);

  // Switch to total tab if current tab becomes hidden
  useEffect(() => {
    const currentTabExists = tabs.some(tab => tab.value === budgetViewTab);

    if (!currentTabExists) {
      setBudgetViewTab('total');
    }
  }, [tabs, budgetViewTab, setBudgetViewTab]);

  return (
    <Sidebar>
      <SidebarHeader>
        <ViewToggle />
      </SidebarHeader>
      <TabsContainer>
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            $isActive={budgetViewTab === tab.value}
            onClick={() => setBudgetViewTab(tab.value)}
          >
            {tab.label}
          </Tab>
        ))}
      </TabsContainer>

      <BudgetPanelContent />
    </Sidebar>
  );
};

const Sidebar = styled.div`
  background-color: ${({ theme }) => theme.palette.brand.white};
  width: 250px;
  flex: 1 0 250px;
  min-height: 0;
  padding: 2rem 0;
  border-radius: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3.5rem;
  width: 100%;
  padding: 0 1.5rem;
`;

const TabsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  padding: 0 2.5rem;
  width: 100%;
  margin-bottom: 1.5rem;
  justify-content: center;
`;

const Tab = styled.button<{ $isActive: boolean }>`
  width: 63px;
  padding: 0.5rem 0.25rem;
  border-radius: 0.5rem;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? '#363649' : theme.palette.brand.white};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.brand.white : theme.palette.brand.textPrimary};
  font-size: 1rem;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: ${({ $isActive }) => ($isActive ? 'none' : '0 0 6px rgba(0, 0, 0, 0.25)')};
`;

export { BudgetSidebar };
